import * as React from 'react';
import type { AssetId, ProjectId } from 'owa-timestream-types';
import { deleteTodo, SPACES_ACTION_SOURCE } from 'owa-timestream-asset-sdk-tasks';
import { getAsset } from 'owa-timestream-store';
import { getAssetDescriptor } from '../assetsCache';
import { useAssetId } from './useAssetId';
import { useProjectId } from './useProjectId';
import deleteAssetsWithConfirm from '../util/deleteAssetsWithConfirm';

/**
 * Use this hook to delete the current asset.
 *
 * Being a hook, this should be called from the rendering function of an asset.
 * The returning function is bound to the ID of the asset being rendered.
 * The end result is that an asset can delete itself without having to know its ID.
 */
export default function useDeleteAsset() {
    const projectId = useProjectId();
    const assetId = useAssetId();
    const deleteAsset: () => void = React.useMemo(
        () => deleteAssetAndTask.bind(null, projectId, assetId),
        [projectId, assetId, deleteAssetsWithConfirm]
    );
    return deleteAsset;
}

async function deleteAssetAndTask(projectId: ProjectId, assetId: AssetId) {
    const { type, taskId } = getAsset(projectId, assetId);
    const descriptor = getAssetDescriptor(type);

    const assetDeleted = await deleteAssetsWithConfirm(projectId, [assetId]);
    if (assetDeleted && !!taskId && !descriptor?.preventFullControlOverTask) {
        deleteTodo(taskId, { actionSource: SPACES_ACTION_SOURCE, viewType: 'Item' });
    }
}
